import { useState } from 'react';
import type { InvestmentPortfolioHolding } from 'shared';

import { useUpdateInvestmentTheme } from '../../features/investmentTheses/portfolioQueries';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

export function InvestmentThemeEditor({ holding }: { holding: InvestmentPortfolioHolding }) {
  const [editing, setEditing] = useState(false);
  const [theme, setTheme] = useState(holding.theme ?? holding.suggestedTheme ?? '');
  const mutation = useUpdateInvestmentTheme();
  const account = holding.accounts[0];
  if (!account) return null;

  function save(value: string | null) {
    if (!account) return;
    mutation.mutate(
      {
        accountId: account.accountId,
        holdingId: account.holdingId,
        theme: value,
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm">{holding.theme ?? 'Unclassified'}</div>
      {!holding.theme && holding.suggestedTheme ? (
        <div className="space-y-1 text-xs text-text-muted">
          <p>Suggested: {holding.suggestedTheme}</p>
          <p>{holding.suggestionReason}</p>
          <Button
            size="sm"
            variant="secondary"
            disabled={mutation.isPending}
            aria-label={`Confirm ${holding.suggestedTheme} theme for ${holding.symbol}`}
            onClick={() => save(holding.suggestedTheme)}
          >
            Confirm suggestion
          </Button>
        </div>
      ) : null}
      {editing ? (
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (theme.trim()) save(theme.trim());
          }}
        >
          <Input
            aria-label={`Theme for ${holding.symbol}`}
            value={theme}
            maxLength={80}
            required
            onChange={(event) => setTheme(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={mutation.isPending || !theme.trim()}>
              Save theme
            </Button>
            {holding.theme ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => save(null)}
              >
                Clear theme
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Edit theme for ${holding.symbol}`}
          onClick={() => {
            setTheme(holding.theme ?? holding.suggestedTheme ?? '');
            setEditing(true);
          }}
        >
          {holding.theme ? 'Edit theme' : 'Assign theme'}
        </Button>
      )}
    </div>
  );
}
